import { useEffect, type ReactNode } from 'react';
import { useDesktopTopBar } from '../../../../context/DesktopLayoutContext';
import { useKioskMediaAccess } from '../../../../redux/mediaManagement/mediaManagementHooks';
import { Bone, ItemListShimmer, ItemToolbarShimmer, LocationGridShimmer } from '../ui/MediaShimmers';
import { MediaManagementShell } from './MediaManagementShell';
import KioskMediaAccessRestricted from './KioskMediaAccessRestricted';

type KioskMediaPage = 'pages' | 'items';

/** The page's frame and shimmer while the permissions are read for the first time. */
function KioskMediaAccessLoading({ page }: { page: KioskMediaPage }) {
  const setTopBarProps = useDesktopTopBar();

  useEffect(() => {
    setTopBarProps({ title: page === 'items' ? 'Items' : 'Pages', breadcrumbs: 'Kiosk Media' });
  }, [setTopBarProps, page]);

  return (
    <MediaManagementShell>
      <div className="mm-page" aria-busy="true">
        <div className="mm-shimmer-row" style={{ gap: 12, marginBottom: 20 }} aria-hidden="true">
          <Bone w={40} h={40} r={10} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Bone w={160} h={20} />
            <Bone w={320} h={12} />
          </div>
        </div>
        {page === 'items' ? (
          <>
            <ItemToolbarShimmer />
            <ItemListShimmer />
          </>
        ) : (
          <LocationGridShimmer />
        )}
      </div>
    </MediaManagementShell>
  );
}

/**
 * Route guard for the Kiosk Media pages (direct URLs too). Permissions are read again on every visit
 * and refresh, so a change made in Employees applies without logging in again. Without View Media
 * (or Edit Media, which implies it) the Access Restricted page is shown.
 */
export function KioskMediaAccessGate({ page, children }: { page: KioskMediaPage; children: ReactNode }) {
  const { status, canView, load } = useKioskMediaAccess();

  useEffect(() => {
    load();
  }, [load]);

  if (status !== 'ready') return <KioskMediaAccessLoading page={page} />;
  return canView ? <>{children}</> : <KioskMediaAccessRestricted />;
}
